import React, { useEffect, useState } from 'react';
import SectionBasePage from 'components/SectionBasePage';
import { useDispatch, useSelector } from 'hooks/store';
import {
  cleanGerminationRoomsAction,
  createGerminationRoomAction,
  germinationRoomsEntitiesSelector,
  germinationRoomsIdsSelector,
  getGerminationRoomListAction,
  isLoadingGerminationRoomsSelector,
  remoteTotalSelector,
  updateGerminationRoomAction,
} from 'state/slices/germinationRoom';
import { usePagination } from 'hooks/pagination';
import { GerminationRoom, PaginationData } from 'constants/interface';
import { Table } from 'UI';
import { Form } from 'antd';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';
import { CreateGerminationRoomData } from 'api/germinationRoom';
import { unwrapResult } from '@reduxjs/toolkit';

import EditModal, { GerminationRoomFormData } from './components/EditModal';
import Extra from './components/Extra';

import {
  ActionButton,
} from './style';
// import { unwrapResult } from '@reduxjs/toolkit';

const GerminationPage = (): JSX.Element => {
  const dispatch = useDispatch();
  const remoteTotal = useSelector(remoteTotalSelector);

  const roomEntities = useSelector(germinationRoomsEntitiesSelector);
  const roomIds = useSelector(germinationRoomsIdsSelector);
  const isRemoteLoading = useSelector(isLoadingGerminationRoomsSelector);
  const [isLoading, setLoading] = useState(false);

  const rooms = roomIds.map((roomId) => {
    const entity = roomEntities[roomId];
    if (!entity) return undefined;
    const tableRoom: GerminationRoom & {
      key?: string,
    } = {
      ...entity,
      key: roomId.toString(),
    };
    return tableRoom;
  }).filter((tableRoom) => !!tableRoom) as (GerminationRoom & { key: string})[];

  const remoteFetch = (
    paginationData: PaginationData,
  ) => {
    dispatch(getGerminationRoomListAction(paginationData));
  };
  const pagination = usePagination<object>( // eslint-disable-line @typescript-eslint/ban-types
    remoteFetch,
    {
      filter: {
        isActive: true,
      },
    },
  );

  useEffect(() => {
    dispatch(cleanGerminationRoomsAction());
  }, []);

  useEffect(() => {
    pagination.updateTotal(remoteTotal);
  }, [remoteTotal]);

  const [germinationRoomForm] = Form.useForm<GerminationRoomFormData>();

  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [activeEditRoom, setActiveEditRoom] = useState<GerminationRoom>();

  const openModal = (roomId: string) => {
    const roomEntity = roomEntities[roomId];
    setActiveEditRoom(roomEntity);
    setEditModalOpen(true);
  };

  const closeModal = () => {
    setActiveEditRoom(undefined);
    setEditModalOpen(false);
    germinationRoomForm.resetFields();
  };

  const submitModal = async () => {
    try {
      await germinationRoomForm.validateFields();
    } catch (e) {
      const errorAlert = createAlertNotification('Verifique os campos com erro.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      return;
    }

    const formData: GerminationRoomFormData = germinationRoomForm.getFieldsValue();

    const roomData: CreateGerminationRoomData = {
      ...formData,
      isActive: true,
    };

    try {
      setLoading(true);
      if (activeEditRoom) {
        const updateResult = await dispatch(updateGerminationRoomAction({
          id: activeEditRoom.id,
          data: {
            ...roomData,
          },
        }));
        unwrapResult(updateResult);
      } else {
        const createResult = await dispatch(createGerminationRoomAction(roomData));
        unwrapResult(createResult);
      }
      const successAlert = createAlertNotification(`
      Câmara de germinação ${activeEditRoom ? 'atualizada' : 'criada'} com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      setLoading(false);
      closeModal();
      pagination.fetch();
    } catch (err) {
      const errorAlert = createAlertNotification('Erro interno ao tentar criar câmara de germinação. Tente novamente.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  return (
    <SectionBasePage extra={<Extra onAddClick={() => openModal('')} />}>
      <>
        <Table
          dataSource={rooms}
          pagination={pagination.getInternal()}
          loading={isRemoteLoading}
        >
          <Table.Column
            title="Nome"
            key="name"
            dataIndex="name"
          />
          <Table.Column
            title="Capacidade"
            key="capacity"
            dataIndex="capacity"
          />
          <Table.Column
            title=""
            width="150px"
            dataIndex="id"
            render={(roomId: string) => (
              <ActionButton onClick={() => openModal(roomId)}>
                editar »
              </ActionButton>
            )}
          />
        </Table>
        <EditModal
          formHandler={germinationRoomForm}
          onSubmit={submitModal}
          onCancel={closeModal}
          curRoom={activeEditRoom}
          isLoading={isLoading}
          isVisible={isEditModalOpen}
        />
      </>
    </SectionBasePage>
  );
};

export default GerminationPage;
